// src/components/CarWash/hooks/useCustomerForm.ts
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import api from "../../../../../features/auth/axiosConfig";
import { useForm } from "./useForm";

interface CustomerFormValues {
  name: string;
  phoneNumber: string;
  date: string;
}

export const useCustomerForm = () => {
  const navigate = useNavigate();
  const now = new Date();
  const today = now.getFullYear() + "-" +  String(now.getMonth() + 1).padStart(2, "0") + "-" +  String(now.getDate()).padStart(2, "0");

  const { values, handleChange, validate } = useForm<CustomerFormValues>(
    {
      name: "",
      phoneNumber: "",
      date: today,
    },
    {
      name: ({ name }) => (!name.trim() ? "Customer name is required." : null),
      date: ({ date }) => (!date ? "Date is required." : null),
    },
  );

  const handleSubmit = async () => {
    const { isValid, error } = validate();
    if (!isValid && error) {
      toast.error(error);
      return;
    }

    try {
      const customerPayload = {
        firstName: values.name,
        middleName: "",
        lastName: "",

        emails: [],

        phones: values.phoneNumber ? [values.phoneNumber] : [],

        addresses: {
          addressLine1: "",
          addressLine2: "",
          city: "",
          postalCode: "",
        },
      };

      const response = await api.post("/app/pos/v2/customer", customerPayload);

      if (!response.data?._id) {
        throw new Error("Invalid response from server");
      }

      /** Shopping cart creation (NO need to resend customer info) */
      const postShoppingResponse = await api.post("/app/pos/v2/shopping_cart", {
        customerId: response.data._id,
        date: values.date,
      });

      toast.success("Car order submitted successfully!");
      navigate(`/v1/car-wash/add-car?id=${postShoppingResponse.data._id}`);
    } catch (error: any) {
      console.error("Error submitting car order:", error);

      const message = error?.response?.data?.message || error?.message || "Failed to submit car order. Please try again.";

      if (message.toLowerCase().includes("phone") && message.toLowerCase().includes("already exists")) {
        toast.error("Customer with this phone number already exists. Please select from existing customers.");
      } else {
        toast.error(message);
      }
    }
  };

  return {
    ...values,
    setName: (val: string) => handleChange("name", val),
    setPhoneNumber: (val: string) => handleChange("phoneNumber", val),
    setDate: (val: string) => handleChange("date", val),
    handleSubmit,
  };
};
